/**
 * MyAttendance.jsx — Personel Puantaj Sayfası (EMPLOYEE)
 * KPI Dashboard + Clock Widget + Takvim + Kayıt Tablosu
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import {
  Clock,
  CalendarDays,
  Play,
  Square,
  Loader2,
  Camera,
  MapPin,
  CalendarCheck,
} from 'lucide-react';
import api from '../../../api/axios';
import AttendanceDashboard from '../components/AttendanceDashboard';
import AttendanceCalendar from '../components/AttendanceCalendar';
import AttendanceTable from '../components/AttendanceTable';
import { localizeDigits } from '../../../utils/localizeNumber';
import useScanner from '../hooks/useScanner';
import { computeStats } from '../utils/attendanceHelpers';

const QR_REGION_ID = 'my-attendance-qr-region';

const MyAttendance = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const numberLocale = isArabic ? 'ar-u-nu-arab' : i18n.language;
  const localizedNumber = (value, options = {}) => localizeDigits(value, i18n.language, options);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'IN' | 'OUT'
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState({ present: 0, late: 0, absent: 0, sick: 0, totalHours: 0, pendingApproval: 0, total: 0 });

  // Scanner hook
  const {
    scannerOpen,
    scannerStatus,
    scannerMessage,
    startScanner,
    closeScanner,
    markSuccess,
    markError,
  } = useScanner({ regionId: QR_REGION_ID, t });

  // Saat güncelleme
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Verileri çek
  const fetchMyRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/attendance/my-records', {
        headers: { 'Accept-Language': i18n.language },
      });
      const data = res.data || [];
      setRecords(data);
      setStats(computeStats(data));
    } catch {
      // toast error handled silently for employee
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchMyRecords();
  }, [fetchMyRecords]);

  // Konum alma
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error(t('err_no_geolocation', 'Tarayıcınız konum özelliğini desteklemiyor.')));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => reject(new Error(t('err_location_denied', 'Konum erişimi reddedildi.'))),
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
      );
    });
  };

  // Clock-in/out işlemi
  const performAction = async (decodedText) => {
    setActionLoading(true);
    try {
      const coords = await getUserLocation();
      const endpoint = pendingAction === 'IN' ? '/attendance/clock-in' : '/attendance/clock-out';

      const res = await api.post(endpoint, {
        latitude: coords.lat,
        longitude: coords.lng,
        qr_data: decodedText,
      }, {
        headers: { 'Accept-Language': i18n.language },
      });

      markSuccess(res.data.message || t('msg_attendance_completed', 'Puantaj işlemi tamamlandı.'));
      await fetchMyRecords();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || t('err_action_failed', 'İşlem başarısız.');
      markError(msg);
    } finally {
      setActionLoading(false);
      setPendingAction(null);
    }
  };

  const openScanner = async (actionType) => {
    setPendingAction(actionType);
    await startScanner(performAction);
  };

  // Takvimde güne tıklama
  const handleDayClick = (dateStr) => {
    // İleride: o günün detaylı popover'ı
    console.log('Day clicked:', dateStr);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500" dir={isArabic ? 'rtl' : 'ltr'}>
      <Toaster position={isArabic ? 'top-left' : 'top-right'} />

      {/* ÜST BÖLÜM: Saat + Clock Butonları + KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 md:gap-4">
        {/* Canlı Saat */}
        <div className="col-span-2 lg:col-span-1 bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2rem] p-4 md:p-5 text-white shadow-xl flex flex-col justify-center relative overflow-hidden group min-h-[110px]">
          <Clock size={90} className="absolute -top-4 -right-4 opacity-5 group-hover:rotate-12 transition-transform duration-700" />
          <p className="text-[9px] md:text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
            <CalendarDays size={12} />
            {currentTime.toLocaleDateString(numberLocale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <div className="text-lg sm:text-xl md:text-3xl font-black tracking-tight" dir="ltr">
            {currentTime.toLocaleTimeString(numberLocale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        </div>

        {/* GİRİŞ Butonu */}
        <button
          onClick={() => openScanner('IN')}
          disabled={actionLoading}
          className="bg-emerald-500 hover:bg-emerald-600 active:scale-95 disabled:opacity-50 rounded-[2rem] px-3 py-3 md:p-5 text-white shadow-xl shadow-emerald-500/30 flex flex-col items-center justify-center gap-1.5 transition-all group relative overflow-hidden min-h-[110px]"
        >
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          {actionLoading && pendingAction === 'IN' ? (
            <Loader2 size={20} className="animate-spin relative z-10" />
          ) : (
            <Play size={20} className="relative z-10" fill="currentColor" />
          )}
          <span className="text-[10px] md:text-xs font-black uppercase tracking-widest relative z-10">
            {t('btn_clock_in_large', 'İŞE BAŞLA')}
          </span>
          <span className="text-[7px] md:text-[8px] font-bold bg-emerald-700/50 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 relative z-10">
            <Camera size={8} /> <MapPin size={8} /> {t('lbl_camera_gps_required', 'KAMERA + GPS')}
          </span>
        </button>

        {/* ÇIKIŞ Butonu */}
        <button
          onClick={() => openScanner('OUT')}
          disabled={actionLoading}
          className="bg-rose-500 hover:bg-rose-600 active:scale-95 disabled:opacity-50 rounded-[2rem] px-3 py-3 md:p-5 text-white shadow-xl shadow-rose-500/30 flex flex-col items-center justify-center gap-1.5 transition-all group relative overflow-hidden min-h-[110px]"
        >
          <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          {actionLoading && pendingAction === 'OUT' ? (
            <Loader2 size={20} className="animate-spin relative z-10" />
          ) : (
            <Square size={20} className="relative z-10" fill="currentColor" />
          )}
          <span className="text-[10px] md:text-xs font-black uppercase tracking-widest relative z-10">
            {t('btn_clock_out_large', 'PAYDOS ET')}
          </span>
          <span className="text-[7px] md:text-[8px] font-bold bg-rose-700/50 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 relative z-10">
            <Camera size={8} /> <MapPin size={8} /> {t('lbl_camera_gps_required', 'KAMERA + GPS')}
          </span>
        </button>

        {/* Toplam Saat Kartı */}
        <div className="bg-white rounded-[2rem] p-5 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center shrink-0">
            <Clock size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              {t('lbl_stat_total_hours', 'TOPLAM SAAT')}
            </p>
            <div className="text-xl font-black text-slate-800 mt-1" dir="ltr">
              {localizedNumber(stats.totalHours, { minimumFractionDigits: 1 })} <span className="text-sm text-slate-400">h</span>
            </div>
          </div>
        </div>

        {/* Devam Günü Kartı */}
        <div className="bg-white rounded-[2rem] p-5 border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center shrink-0">
            <CalendarCheck size={24} />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              {t('lbl_stat_present', 'DEVAM GÜNÜ')}
            </p>
            <div className="text-xl font-black text-slate-800 mt-1">
              {localizedNumber(stats.present)}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Dashboard Kartları */}
      <AttendanceDashboard stats={stats} role="EMPLOYEE" />

      {/* QR Scanner Modal */}
      {scannerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">
                {pendingAction === 'IN' ? t('btn_clock_in', 'Giriş Yap') : t('btn_clock_out', 'Çıkış Yap')}
              </h3>
              <button
                onClick={closeScanner}
                className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 transition-colors"
              >
                ✕
              </button>
            </div>
            <div id={QR_REGION_ID} className="rounded-2xl overflow-hidden" />
            <p className={`mt-3 text-center text-xs font-bold ${
              scannerStatus === 'success' ? 'text-emerald-600' :
              scannerStatus === 'error' ? 'text-rose-600' :
              'text-slate-500'
            }`}>
              {scannerMessage}
            </p>
          </div>
        </div>
      )}

      {/* Takvim Görünümü */}
      <AttendanceCalendar
        records={records}
        defaultView="monthly"
        onDayClick={handleDayClick}
      />

      {/* Kayıt Tablosu */}
      <AttendanceTable
        records={records}
        loading={loading}
        showEmployee={false}
        showApproval={false}
        showCheckbox={false}
      />
    </div>
  );
};

export default MyAttendance;